import { useEffect, useId, useRef, useState } from 'react'
import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'

import { CheckIcon, ChevronDownIcon, FlagGBIcon, FlagITIcon, FlagTRIcon } from '../components/icons'
import { saveLocale } from '../lib/settings'
import { LOCALES, setLocale } from './index'

/** Her dilin bayragi - eklenmeyen bir dil bayraksiz, sadece metinle calisir. */
const LOCALE_FLAGS: Partial<Record<string, ComponentType<{ size?: number }>>> = {
  tr: FlagTRIcon,
  en: FlagGBIcon,
  it: FlagITIcon,
}

/**
 * Bayragi OLMAYAN diller icin rozet harfi. Arapca 20'den fazla ulkenin dili;
 * birinin bayragini secmek digerlerini disarida birakir ve siyasi bir
 * tercih gibi okunur. Bunun yerine dilin kendi alfabesinden bir harf.
 */
const LOCALE_GLYPHS: Partial<Record<string, string>> = {
  ar: 'ع',
}

/**
 * Yuvarlak bayrak rozeti. Dikdortgen bayrak SVG'si (20x14) daireyi
 * "cover" edecek kadar buyutulup ortadan kirpiliyor; ince ic halka acik
 * zeminde beyaz alanli bayraklarin kenarini belli ediyor.
 */
function FlagBadge({ code }: { code: string }) {
  const Flag = LOCALE_FLAGS[code]
  if (!Flag) {
    const glyph = LOCALE_GLYPHS[code]
    return glyph ? (
      <span className="lang-flag lang-flag-glyph" aria-hidden="true">
        {glyph}
      </span>
    ) : null
  }
  return (
    <span className="lang-flag" aria-hidden="true">
      <Flag size={30} />
    </span>
  )
}

/**
 * Dil secici.
 *
 * ## Native `<select>` DEGIL, ozel bir listbox
 *
 * Onceki hali native `<select>` kullaniyordu: klavye gezinmesini, ekran
 * okuyucu duyurusunu ve mobilde platformun kendi secicisini BEDAVA
 * veriyordu. Ama native `<option>` SVG/resim GOSTEREMIYOR - bu tarayici
 * kisitidir, tasarim tercihi degil. Bayragi acik listede de gostermek
 * istegi bu sinira carpiyor, o yuzden `role="listbox"`/`role="option"` ile
 * elle yazilmis bir listbox'a gecildi (ayni desen: `ChatScreen.tsx`daki
 * `ProfileMenu`, `Sidebar.tsx`daki konusma menusu - tikla-ac, disari
 * tiklayinca kapan).
 *
 * ## Etiketler dilin KENDI adinda
 *
 * "English" yaziyor, "Ingilizce" degil. Ingilizce arayan bir kullanici
 * Turkce bilmiyor; listeyi Turkce yazmak, onun aradigi satiri okuyamamasi
 * demek. Bu, dil secicilerinde en sik yapilan hata.
 *
 * ## Sunucuya kaydetmek OPSIYONEL
 *
 * Giris ekraninda oturum yok: `persist` kapali, secim yalnizca tarayicida
 * kaliyor. Giris yapmis kullanicida ayar hesaba yaziliyor ki diger
 * cihazlarda da gecerli olsun.
 *
 * Sunucuya yazma BASARISIZ olursa dil YINE degisiyor ve yalnizca kucuk bir
 * uyari cikiyor: kullanicinin gordugu dil, bir ag hatasi yuzunden geri
 * alinmamali. Katalog indirilemezse ise dil DEGISMIYOR (`setLocale` false
 * donuyor) - yarim cevrilmis bir ekran gostermek yerine.
 */
export function LanguagePicker({
  persist = false,
  variant = 'full',
  showHint = true,
  placement = 'up',
}: {
  persist?: boolean
  variant?: 'full' | 'compact'
  /** Ayarlar ekraninda aciklama bolum basliginda duruyor - iki kez YAZILMAZ. */
  showHint?: boolean
  /** Kompakt menunun acilis yonu: altliklarda (sidebar) yukari, ust barda
   *  (ana sayfa) asagi - aksi halde menu ekranin disina tasiyor. */
  placement?: 'up' | 'down'
}) {
  const { t, i18n } = useTranslation()
  const labelId = useId()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function close(event: MouseEvent) {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  async function change(next: string) {
    setOpen(false)
    setBusy(true)
    setFailed(false)
    try {
      const ok = await setLocale(next)
      if (!ok) {
        setFailed(true)
        return
      }
      if (persist) {
        // Basarisizlik dili GERI ALMIYOR - yukaridaki gerekce.
        await saveLocale(next).catch(() => setFailed(true))
      }
    } finally {
      setBusy(false)
    }
  }

  const currentCode = i18n.language in LOCALES ? i18n.language : Object.keys(LOCALES)[0]
  const currentLabel = LOCALES[currentCode]?.label ?? currentCode

  const trigger = (
    <button
      type="button"
      className="lang-picker-trigger"
      onClick={() => setOpen((value) => !value)}
      disabled={busy}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-labelledby={variant === 'full' ? labelId : undefined}
      aria-label={variant === 'compact' ? t('settings.language.label') : undefined}
    >
      <FlagBadge code={currentCode} />
      {variant === 'full' ? (
        <span className="lang-picker-trigger-label">{currentLabel}</span>
      ) : (
        <span className="lang-picker-trigger-code">{currentCode.toUpperCase()}</span>
      )}
      <span className="lang-picker-trigger-chevron" aria-hidden="true">
        <ChevronDownIcon size={13} />
      </span>
    </button>
  )

  const listbox = open && (
    <div className="lang-picker-menu" role="listbox" aria-label={t('settings.language.label')}>
      {Object.entries(LOCALES).map(([code, info]) => {
        const selected = code === currentCode
        return (
          <button
            key={code}
            type="button"
            role="option"
            aria-selected={selected}
            className={selected ? 'lang-picker-option lang-picker-option-active' : 'lang-picker-option'}
            onClick={() => void change(code)}
          >
            <FlagBadge code={code} />
            <span>{info.label}</span>
            {selected && (
              <span className="lang-picker-option-check" aria-hidden="true">
                <CheckIcon size={13} />
              </span>
            )}
          </button>
        )
      })}
    </div>
  )

  if (variant === 'compact') {
    return (
      <div
        className={
          placement === 'down'
            ? 'lang-picker lang-picker-compact lang-picker-down'
            : 'lang-picker lang-picker-compact'
        }
        ref={root}
      >
        {trigger}
        {listbox}
      </div>
    )
  }

  return (
    <div className="lang-picker" ref={root}>
      <span className="lang-picker-label" id={labelId}>
        {t('settings.language.label')}
      </span>
      <div className="lang-picker-control">
        {trigger}
        {listbox}
      </div>
      {showHint && <p className="field-hint">{t('settings.language.hint')}</p>}
      {failed && (
        <p className="form-error" role="alert">
          {t('settings.language.failed')}
        </p>
      )}
    </div>
  )
}
